import { toProspectPortalHomeUrl } from '@common/services';
import type { LeasingJourneyTheme } from '@common/ui';

import {
  buildLaunchSelectionForSession,
  LAUNCH_SELECTION_SESSION_KEY,
  readLaunchSelectionContextFromSession,
} from './floorPlanLaunchSelection';

export const LAUNCH_INSTALLMENT_PLAN_SESSION_KEY = 'launchInstallmentPlanId';

/**
 * Payload shape returned by GET /v1/viewer/launch-context (mirrors API launch DTO fields used in the viewer).
 */
export interface ViewerLaunchContextPayload {
  orgId: number;
  propertyId: number;
  rwxDomain: string;
  websiteId?: string;
  entrataRedirectLink: string;
  locale?: string;
  propertyFloorplanId?: number;
  unitSpaceId?: number;
  termMonth?: string;
  leaseStartDate?: string;
  leaseTermId?: number;
  leaseStartWindowId?: number;
  installmentPlanId?: number;
  spaceConfigurationId?: number;
  selectedOccupancyTypeId?: number;
  fromWaitlist?: boolean;
  desiredMoveInDate?: string;
  returningApplicant?: boolean;
  theme?: LeasingJourneyTheme;
}

/**
 * Writes launch-context API data to sessionStorage (queryParams, entrataRedirectLink, floor-plan selection).
 */
export function applyLaunchContextToSessionStorage(data: ViewerLaunchContextPayload): void {
  // eslint-disable-next-line no-console
  console.log(
    `[viewer-launch] applying launch context to sessionStorage — orgId=${data.orgId}, propertyId=${data.propertyId}`,
  );
  // eslint-disable-next-line no-console
  console.log(`[viewer-launch] launch context payload: ${JSON.stringify(data)}`);

  const link = data.entrataRedirectLink
    ? toProspectPortalHomeUrl(String(data.entrataRedirectLink))
    : '';
  if (link) {
    sessionStorage.setItem('entrataRedirectLink', link);
  }
  const { websiteId, rwxDomain } = data;
  if (data.orgId > 0 && websiteId != null && String(websiteId) !== '') {
    const previous = readBrandingParamsFromSessionStorage();
    if (
      previous &&
      (previous.orgId !== String(data.orgId) || previous.websiteId !== String(websiteId))
    ) {
      sessionStorage.removeItem('brandingColors');
      sessionStorage.removeItem('brandingMedia');
    }
    const queryParams = {
      websiteId: String(websiteId),
      propertyId: data.propertyId != null ? String(data.propertyId) : undefined,
      rwxDomain: String(rwxDomain),
      orgId: data.orgId != null ? String(data.orgId) : undefined,
      entrataRedirectLink: link || undefined,
    };
    sessionStorage.setItem('queryParams', JSON.stringify(queryParams));
  } else {
    sessionStorage.removeItem('queryParams');
    sessionStorage.removeItem('brandingColors');
    sessionStorage.removeItem('brandingMedia');
  }

  const selection = buildLaunchSelectionForSession(data);
  if (selection) {
    sessionStorage.setItem(LAUNCH_SELECTION_SESSION_KEY, JSON.stringify(selection));
    // eslint-disable-next-line no-console
    console.log(
      `[viewer-launch] stored ${LAUNCH_SELECTION_SESSION_KEY}: ${JSON.stringify(selection)}`,
    );
  } else {
    sessionStorage.removeItem(LAUNCH_SELECTION_SESSION_KEY);
    // eslint-disable-next-line no-console
    console.log(
      `[viewer-launch] no floor-plan selection — removed ${LAUNCH_SELECTION_SESSION_KEY}`,
    );
  }

  if (
    typeof data.installmentPlanId === 'number' &&
    Number.isInteger(data.installmentPlanId) &&
    data.installmentPlanId >= 1
  ) {
    sessionStorage.setItem(LAUNCH_INSTALLMENT_PLAN_SESSION_KEY, String(data.installmentPlanId));
  } else {
    sessionStorage.removeItem(LAUNCH_INSTALLMENT_PLAN_SESSION_KEY);
  }

  // eslint-disable-next-line no-console
  console.log('[viewer-launch] sessionStorage hydrate complete', {
    entrataRedirectLink: sessionStorage.getItem('entrataRedirectLink'),
    queryParams: sessionStorage.getItem('queryParams'),
    launchSelectionContext: sessionStorage.getItem(LAUNCH_SELECTION_SESSION_KEY),
  });
}

/** Removes one-time floor-plan / waitlist prefill (safe to call on logout or after apply). */
export function clearLaunchSelectionFromSession(): void {
  sessionStorage.removeItem(LAUNCH_SELECTION_SESSION_KEY);
  sessionStorage.removeItem(LAUNCH_INSTALLMENT_PLAN_SESSION_KEY);
}

/** Removes only the separately stored one-time installment-plan prefill. */
export function clearLaunchInstallmentPlanSelectionFromSession(): void {
  sessionStorage.removeItem(LAUNCH_INSTALLMENT_PLAN_SESSION_KEY);
}

/**
 * Clears one-time floor-plan/waitlist values after they are applied while retaining
 * the separately stored installment-plan launch selection until Billing data is available.
 */
export function clearAppliedFloorPlanSelectionFromSession(): void {
  // The student page reads the launch context after this generic initializer has
  // completed. Keep the complete student context until the student selector has
  // consumed it or the applicant changes their selection.
  if (readLaunchSelectionContextFromSession()?.spaceConfigurationId != null) return;

  sessionStorage.removeItem(LAUNCH_SELECTION_SESSION_KEY);
}

export function readLaunchInstallmentPlanIdFromSession(): number | undefined {
  const value = Number(sessionStorage.getItem(LAUNCH_INSTALLMENT_PLAN_SESSION_KEY));
  return Number.isInteger(value) && value >= 1 ? value : undefined;
}

export interface BrandingQueryParams {
  orgId?: string;
  websiteId?: string;
  propertyId?: string;
  rwxDomain?: string;
}

/**
 * Branding params after launch-context hydrate (GET /viewer/launch-context → sessionStorage).
 */
export function readBrandingParamsFromSessionStorage(): BrandingQueryParams | null {
  const raw = sessionStorage.getItem('queryParams');
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as BrandingQueryParams;
    if (p.orgId && p.websiteId) {
      return {
        orgId: p.orgId,
        websiteId: p.websiteId,
        propertyId: p.propertyId,
        rwxDomain: p.rwxDomain,
      };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Reads a single field from the JSON stored at `queryParams` (set by launch-context hydrate).
 * Used when the viewer is opened with path only after POST /v1/viewer/launch (DB handoff).
 */
export function readStoredLaunchQueryParam(
  key: 'websiteId' | 'propertyId' | 'rwxDomain' | 'orgId',
): string {
  const raw = sessionStorage.getItem('queryParams');
  if (!raw) return '';
  try {
    const p = JSON.parse(raw) as Record<string, unknown>;
    const v = p[key];
    return v != null ? String(v) : '';
  } catch {
    return '';
  }
}

/** Decoded prospect portal home URL from session (origin only). */
export function readEntrataRedirectLinkFromSession(): string {
  const raw = sessionStorage.getItem('entrataRedirectLink') || '';
  return raw ? toProspectPortalHomeUrl(raw) : '';
}

/**
 * Resolves the prospect portal base URL for legacy redirect and launch flows.
 * Checks session, URL query param, then queryParams JSON; persists URL values to session.
 */
export function resolveEntrataRedirectLink(
  searchParams?: URLSearchParams | null,
): string | undefined {
  const fromSession = readEntrataRedirectLinkFromSession();
  if (fromSession) {
    return fromSession;
  }

  const fromUrl = searchParams?.get('entrataRedirectLink');
  if (fromUrl) {
    const normalized = toProspectPortalHomeUrl(fromUrl);
    if (normalized) {
      sessionStorage.setItem('entrataRedirectLink', normalized);
      return normalized;
    }
  }

  const raw = sessionStorage.getItem('queryParams');
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { entrataRedirectLink?: string };
      if (parsed.entrataRedirectLink) {
        return toProspectPortalHomeUrl(parsed.entrataRedirectLink);
      }
    } catch {
      /* ignore invalid JSON */
    }
  }

  return undefined;
}

/** Builds the temporary ProspectPortal privacy notice URL from the normalized site origin. */
export function buildProspectPortalPrivacyNoticeUrl(
  prospectPortalOrigin?: string,
): string | undefined {
  if (!prospectPortalOrigin) return undefined;

  try {
    return new URL('/privacy-policy/', prospectPortalOrigin).toString();
  } catch {
    return undefined;
  }
}

/** Validates a third-party privacy notice URL passed on the viewer URL. */
export function resolvePrivacyNoticeUrl(privacyNoticeUrl?: string | null): string | undefined {
  if (!privacyNoticeUrl) return undefined;

  const value = privacyNoticeUrl.trim();
  if (!value) return undefined;

  try {
    const url = new URL(value);

    return url.protocol === 'https:' ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}
